import { useState } from "react";
import { Button, Checkbox, Icon, Label } from "semantic-ui-react";

import { Can, DetailsCard, InterventionModal, ROBServiceRequest, useCan } from "components";
import { useShowPriceInVat } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES, STATUS_IDENTIFIERS } from "models";
import { AppointmentReportDropdown, TotalPriceBar } from "modules/AppointmentDetails/components";
import { Intervention } from "modules/AppointmentDetails/components/Interventions/components";
import { useInterventions } from "modules/AppointmentDetails/components/Interventions/hooks";
import { INTERVENTIONS_STORAGE_KEYS } from "modules/AppointmentDetails/components/Interventions/storageKeys";
import { useAppointmentDealer, useRealTimeOrderedChecks } from "modules/AppointmentDetails/hooks";
import { canWriteBackCustomerData, isCarReady, isCarReadyOrQualityCheck } from "utils/appointmentUtils";
import { getPreference, setPreference } from "utils/common";

import type { SectionMenu } from "components";
import type { Appointment, Checklist, DealerLocation, Intervention as InterventionModel } from "models";
import type { Dispatch, FormEvent, SetStateAction } from "react";
import type { CheckboxProps } from "semantic-ui-react";

type InterventionsProps = {
  sectionMenu: SectionMenu;
  isExpanded: boolean;
  hasChanges: boolean;
  appointment: Appointment;
  appointmentLocation: DealerLocation;
  setHasChanges: Dispatch<SetStateAction<boolean>>;
  checklists?: Checklist[];
};

const setInitialValueForCustomerOkFilter = () => getPreference(INTERVENTIONS_STORAGE_KEYS.InterventionsCustomerOkFilterPreference, false);

export const Interventions = ({ isExpanded, appointment, appointmentLocation, sectionMenu, hasChanges, setHasChanges, checklists }: InterventionsProps) => {
  const [customerOkFilterChecked, setCustomerOkFilterChecked] = useState(setInitialValueForCustomerOkFilter);

  const appointmentDealer = useAppointmentDealer(appointment);

  const {
    activeTab,
    interventionTotals,
    interventionModal,
    diagnoseOverviewEvent,
    convertedInterventions,
    localAndDmsInterventions,
    setActiveTab,
    handleRequestClose,
    handleOpenInterventionModal
  } = useInterventions(appointment);
  const { checks } = useRealTimeOrderedChecks(appointment.id);
  const showPriceInVat = useShowPriceInVat();
  const canUpdateAppointments = useCan("update", "appointments");

  const handleUpdateInterventionsFilter = (customerOkFilterChecked: boolean) => {
    setCustomerOkFilterChecked(customerOkFilterChecked);
    setPreference(INTERVENTIONS_STORAGE_KEYS.InterventionsCustomerOkFilterPreference, customerOkFilterChecked);
  };

  const handleUpdateCustomerOkFilterCheckbox = (evt: FormEvent<HTMLInputElement>, { checked = false }: CheckboxProps) => {
    evt.stopPropagation();
    handleUpdateInterventionsFilter(checked);
  };

  const customerOkFilterPreference = getPreference(INTERVENTIONS_STORAGE_KEYS.InterventionsCustomerOkFilterPreference, customerOkFilterChecked);
  const filterCustomerOkInterventions = (interventions?: InterventionModel[]) =>
    !customerOkFilterPreference || isCarReadyOrQualityCheck(appointment.appointment_status_identifier)
      ? interventions
      : interventions?.filter(intervention => intervention.customer_ok);

  const localAndDmsInterventionsToDisplay = filterCustomerOkInterventions(localAndDmsInterventions);
  const convertedInterventionsToDisplay = filterCustomerOkInterventions(convertedInterventions);
  const shouldDisableCustomerOkFilterCheckbox =
    (!localAndDmsInterventions.length && !convertedInterventions.length) ||
    (!localAndDmsInterventions.some(intervention => !intervention.customer_ok) && !convertedInterventions.some(intervention => !intervention.customer_ok));

  return (
    <>
      <DetailsCard
        sectionMenuId={sectionMenu.id}
        title={sectionMenu.count ? `${sectionMenu.title} (${sectionMenu.count})` : sectionMenu.title}
        icon={sectionMenu.icon}
        isExpanded={isExpanded}
        rightContent={
          <div className="interventions-details-card-right-content">
            {!isCarReady(appointment.appointment_status_identifier) &&
              appointment.appointment_status_identifier !== STATUS_IDENTIFIERS.CanceledStatus &&
              canUpdateAppointments &&
              appointmentLocation.manually_create_intervention_is_enabled && (
                <Can I="create" the="interventions">
                  <div className="full between add-intervention-button-color">
                    <Button className="-appointment-status icon new-intervention" onClick={handleOpenInterventionModal}>
                      <Icon className="plus" size="big" />
                    </Button>
                  </div>
                </Can>
              )}

            <Can I={ACL_ACTIVITIES.List} the={ACL_RESOURCES.ROBNetServiceRequests}>
              <ROBServiceRequest appointment={appointment} appointmentLocation={appointmentLocation} />
            </Can>

            {!isCarReadyOrQualityCheck(appointment.appointment_status_identifier) && (
              <Label basic className="intervention-customer-ok-toggle-contaier">
                <Icon className="check circle intervention-customer-ok-toggle-icon" color="green" />
                <Checkbox
                  disabled={shouldDisableCustomerOkFilterCheckbox}
                  className="intervention-customer-ok-toggle"
                  toggle
                  checked={customerOkFilterChecked}
                  onChange={handleUpdateCustomerOkFilterCheckbox}
                />
              </Label>
            )}

            <AppointmentReportDropdown appointment={appointment} />
          </div>
        }
      >
        <div className="Interventions">
          {!!localAndDmsInterventionsToDisplay?.length && (
            <div className="Interventions-container">
              {localAndDmsInterventionsToDisplay.map((item, index) => (
                <Intervention
                  appointment={appointment}
                  appointmentLocation={appointmentLocation}
                  checks={checks}
                  key={item.id}
                  index={index}
                  intervention={item}
                  hasChanges={hasChanges}
                  checklists={checklists}
                  activeIntervention={activeTab}
                  diagnoseOverviewEvent={diagnoseOverviewEvent}
                  setHasChanges={setHasChanges}
                  onRequestCloseTabs={setActiveTab}
                />
              ))}
            </div>
          )}

          {!!convertedInterventionsToDisplay?.length && (
            <div className="Interventions-container">
              {convertedInterventionsToDisplay.map((item, index) => (
                <Intervention
                  appointment={appointment}
                  appointmentLocation={appointmentLocation}
                  checks={checks}
                  key={item.id}
                  index={localAndDmsInterventions.length + index}
                  intervention={item}
                  hasChanges={hasChanges}
                  activeIntervention={activeTab}
                  diagnoseOverviewEvent={diagnoseOverviewEvent}
                  setHasChanges={setHasChanges}
                  onRequestCloseTabs={setActiveTab}
                  checklists={checklists}
                />
              ))}
            </div>
          )}

          <TotalPriceBar appointment={appointment} interventionTotals={interventionTotals} showPriceInVat={showPriceInVat} />
        </div>
      </DetailsCard>

      <InterventionModal
        appointmentId={appointment.id}
        carId={appointment.car_id}
        open={interventionModal}
        onClose={handleRequestClose}
        location={appointmentLocation}
        dealer={appointmentDealer}
        canWriteBackCustomerData={canWriteBackCustomerData(appointmentLocation)}
      />
    </>
  );
};
